import { faXmark } from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useState } from "react";
import { Button, Card, Offcanvas, ToggleButton, ToggleButtonGroup } from "react-bootstrap";

import {
  IResidentialQsnResponse,
  IResidentialQuestion,
  ISelectedQueResp
} from "../../../../config/interfaces";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import { FormField, SelectInputField } from "../../../fields";

type Props = {
	showCanvas: boolean;
	formValues: any;
	handleCloseBtnPress: (status: string | null) => void;
};

const partialFY = UtilService.createFinancialYear("partial");
const fullFY = UtilService.createFinancialYear("full");
const last7FY = `1 Apr ${new Date().getFullYear() - 8} to 31 Mar ${new Date().getFullYear() - 1}`;
const last4FY = `1 Apr ${new Date().getFullYear() - 5} to 31 Mar ${new Date().getFullYear() - 1}`;

const residentialQuestions: IResidentialQuestion[] = [
	{
		id: 1,
		question: `For how many days were you in India in FY ${fullFY}?`,
		answers: [
			{
				label: "Less than 60 days",
				value: null,
				residentialStatus: "Non-Resident"
			},
			{ label: "60-119 days", value: 2 },
			{ label: "120-181 days", value: 8 },
			{ label: "182 days or more", value: 13 }
		],
		type: "dropdown",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check the number of days stayed? - One way is to check your travel tickets and
							calculate the number of days you stayed in India during the Financial Year
						</li>
						<li className="mb-3">
							You don't need to worry about which Residential Status to select. Just answer a few
							questions and we will auto-select it for you.
						</li>
					</ul>
					<p className="primary-para text-greyDark mb-0">
						For more information on Residential Status on Income Tax, you can go through this
						article Example: If your have stayed in India for less than 60 days, as per the ITD
						rules, your residential status will be auto-selected as "Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 2,
		question: `Did you leave India for employment purposes or as a crew on a ship in FY ${fullFY}?`,
		answers: [
			{ label: "Yes", value: null, residentialStatus: "Non-Resident" },
			{ label: "No", value: 14 }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<p className="primary-para text-greyDark mb-0">Select Yes if either</p>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">You left India for employment outside or</li>
						<li className="mb-3">You were a crew member on a ship that sailed outside India</li>
					</ul>
					<p className="primary-para text-greyDark">Select No for any other scenario.</p>
					<p className="primary-para text-greyDark mb-0">
						Example: If your have stayed in India for 60-181 days and you left India for employment,
						then as per the ITD rules, your residential status will be auto-selected as
						"Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 3,
		question: `Did you stay in India for 365 days or more in the last 4 Financial Years (${last4FY})?`,
		answers: [
			{ label: "Yes", value: null, residentialStatus: "Resident" },
			{ label: "No", value: null, residentialStatus: "Resident but not Ordinary Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<p className="primary-para text-greyDark mb-0">Tip:</p>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check the number of days stayed? - One way is to check your travel tickets and
							calculate the number of days you stayed in India during the last 4 Financial Years
							starting {last4FY}
						</li>
					</ul>
					<p className="primary-para text-greyDark mb-0">
						Select Yes if the days stayed in India in this time frame is more than or equal to 365.
					</p>
					<p className="primary-para text-greyDark">Select No if it is less than 365.</p>
					<p className="primary-para text-greyDark mb-0">
						Example: If your have stayed in India for 60-119 days in FY {partialFY} but less than
						365 days in the last 4 Financial Years, then as per the ITD rules, your residential
						status will be auto-selected as "Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 4,
		question: `Were you a tax resident in India in more than 1 Financial Year out of 10 Financial Years prior to FY ${partialFY}?`,
		answers: [
			{ label: "Yes", value: null, residentialStatus: "Resident but not Ordinary Resident" },
			{ label: "No", value: null, residentialStatus: "Resident but not Ordinary Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check if you were a tax resident in India for more than 1 out of the previous
							10 Financial Years?
							<ul>
								<li className="mb-3">
									Option 1: If you stayed in India for more than 181 days in each FY for minimum 2
									or more Financial Years before FY ${partialFY}, then we can fairly suggest to
									select <b>Yes</b>. If this is true only for 0 or 1 FY, then select <b>No</b>.
								</li>
								<li className="mb-3">
									Option 2: Check your previous years' Income Tax Returns forms from E-filing portal
									and answer the question accordingly.
								</li>
							</ul>
						</li>
						<li className="mb-3">
							What if you have filed your tax returns for less than 10 Financial Years as you didn't
							have income for so long - No need to worry. For such case too, just answer the
							question in the best possible way, which is as per the actual number of Tax returns
							filed.
						</li>
					</ul>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 5,
		question: `Were you in India for 730 days or more in the last 7 Financial Years (${last7FY})?`,
		answers: [
			{ label: "Yes", value: null, residentialStatus: "Resident" },
			{ label: "No", value: null, residentialStatus: "Resident but not Ordinary Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check the number of days stayed? - One way is to check your travel tickets and
							calculate the number of days you stayed in India during the last 7 Financial Years
							starting {last7FY}
						</li>
						<li className="mb-3">
							Select <b>Yes</b> if the days stayed in India in this time frame is more than or equal
							to 730.
						</li>
						<li className="mb-3">
							Select <b>No</b> if it is less than 730.
						</li>
					</ul>
					<p className="primary-para text-greyDark">
						Example 1: If your have stayed in India for 730 days or more between {last7FY}, then as
						per the ITD rules, your residential status will be auto-selected as "Resident".
					</p>
					<p className="primary-para text-greyDark mb-0">
						Example 2: If your have stayed in India for less than 730 days between {last7FY}, then
						as per the ITD rules, your residential status will be auto-selected as "Resident - Not
						Ordinary Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 6,
		question: "Please select which one of the following is applicable for you",
		answers: [
			{
				label: "Person of Indian origin but a citizen of another country",
				value: 7
			},
			{ label: "Indian citizen with foreign employment", value: 9 },
			{
				label: "Indian citizen but not a tax resident in any other country",
				value: 10
			},
			{
				label: "Foreign citizen visiting or coming to India for employment",
				value: 12
			}
		],
		type: "dropdown",
		instructions: null
	},
	{
		id: 7,
		question: `Was your Indian income more than Rs. 15L in FY ${fullFY}?`,
		answers: [
			{ label: "Yes", value: 3 },
			{ label: "No", value: null, residentialStatus: "Non-Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							Select <b>Yes</b> if your earnings in India for FY {partialFY} is more than Rs 15
							Lakhs
						</li>
						<li className="mb-3">
							Select <b>No</b> if your earnings in India for FY {partialFY} is less than Rs 15 Lakhs
						</li>
						<li className="mb-3">
							How to check the Indian income? - You can sum up all your earnings from India in the
							FY like salary, rental income, dividend income from Indian market, etc.
						</li>
					</ul>
					<p className="primary-para text-greyDark mb-0">
						Example: If your Indian income in this scenario is less than Rs 15 Lakhs, then as per
						the ITD rules, your residential status will be auto-selected as "Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 8,
		question: `Did you leave India for employment purposes or as a crew on a ship in FY ${fullFY}?`,
		answers: [
			{ label: "Yes", value: null, residentialStatus: "Non-Resident" },
			{ label: "No", value: 6 }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<p className="primary-para text-greyDark mb-0">Select Yes if either</p>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">You left India for employment outside or</li>
						<li className="mb-3">You were a crew member on a ship that sailed outside India</li>
					</ul>
					<p className="primary-para text-greyDark">Select No for any other scenario.</p>
					<p className="primary-para text-greyDark mb-0">
						Example: If your have stayed in India for 60-181 days and you left India for employment,
						then as per the ITD rules, your residential status will be auto-selected as
						"Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 9,
		question: `Was your Indian income more than Rs. 15L in FY ${fullFY}?`,
		answers: [
			{ label: "Yes", value: 3 },
			{ label: "No", value: null, residentialStatus: "Non-Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							Select <b>Yes</b> if your earnings in India for FY {partialFY} is more than Rs 15
							Lakhs
						</li>
						<li className="mb-3">
							Select <b>No</b> if your earnings in India for FY {partialFY} is less than Rs 15 Lakhs
						</li>
						<li className="mb-3">
							How to check the Indian income? - You can sum up all your earnings from India in the
							FY like salary, rental income, dividend income from Indian market, etc.
						</li>
					</ul>
					<p className="primary-para text-greyDark mb-0">
						Example: If your Indian income in this scenario is less than Rs 15 Lakhs, then as per
						the ITD rules, your residential status will be auto-selected as "Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 10,
		question: `Was your Indian income more than Rs. 15L in FY ${fullFY}?`,
		answers: [
			{ label: "Yes", value: 11 },
			{ label: "No", value: 12 }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							Select <b>Yes</b> if your earnings in India for FY {partialFY} is more than Rs 15
							Lakhs
						</li>
						<li className="mb-3">
							Select <b>No</b> if your earnings in India for FY {partialFY} is less than Rs 15 Lakhs
						</li>
						<li className="mb-3">
							How to check the Indian income? - You can sum up all your earnings from India in the
							FY like salary, rental income, dividend income from Indian market, etc.
						</li>
					</ul>
					<p className="primary-para text-greyDark mb-0">
						Example: If your Indian income in this scenario is less than Rs 15 Lakhs, then as per
						the ITD rules, your residential status will be auto-selected as "Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 11,
		question: `Did you stay in India for 365 days or more in the last 4 Financial Years (${last4FY})?`,
		answers: [
			{ label: "Yes", value: null, residentialStatus: "Resident" },
			{ label: "No", value: 4 }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<p className="primary-para text-greyDark mb-0">Tip:</p>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check the number of days stayed? - One way is to check your travel tickets and
							calculate the number of days you stayed in India during the last 4 Financial Years
							starting {last4FY}
						</li>
					</ul>
					<p className="primary-para text-greyDark mb-0">
						Select Yes if the days stayed in India in this time frame is more than or equal to 365.
					</p>
					<p className="primary-para text-greyDark">Select No if it is less than 365.</p>
					<p className="primary-para text-greyDark mb-0">
						Example: If your have stayed in India for 60-119 days in FY {partialFY} but less than
						365 days in the last 4 Financial Years, then as per the ITD rules, your residential
						status will be auto-selected as "Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 12,
		question: `Did you stay in India for 365 days or more in the last 4 Financial Years (${last4FY})?`,
		answers: [
			{ label: "Yes", value: 13 },
			{ label: "No", value: null, residentialStatus: "Non-Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<p className="primary-para text-greyDark mb-0">Tip:</p>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check the number of days stayed? - One way is to check your travel tickets and
							calculate the number of days you stayed in India during the last 4 Financial Years
							starting {last4FY}
						</li>
					</ul>
					<p className="primary-para text-greyDark mb-0">
						Select Yes if the days stayed in India in this time frame is more than or equal to 365.
					</p>
					<p className="primary-para text-greyDark">Select No if it is less than 365.</p>
					<p className="primary-para text-greyDark mb-0">
						Example: If your have stayed in India for 60-119 days in FY {partialFY} but less than
						365 days in the last 4 Financial Years, then as per the ITD rules, your residential
						status will be auto-selected as "Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 13,
		question: `Were you a tax resident in India in more than 1 Financial Year out of 10 Financial Years prior to FY ${partialFY}?`,
		answers: [
			{ label: "Yes", value: 5 },
			{ label: "No", value: null, residentialStatus: "Resident but not Ordinary Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check if you were a tax resident in India for more than 1 out of the previous
							10 Financial Years?
							<ul>
								<li className="mb-3">
									Option 1: If you stayed in India for more than 181 days in each FY for minimum 2
									or more Financial Years before FY {partialFY}, then we can fairly suggest to
									select <b>Yes</b>. If this is true only for 0 or 1 FY, then select <b>No</b>.
								</li>
								<li className="mb-3">
									Option 2: Check your previous years' Income Tax Returns forms from E-filing portal
									and answer the question accordingly.
								</li>
							</ul>
						</li>
						<li className="mb-3">
							What if you have filed your tax returns for less than 10 Financial Years as you didn't
							have income for so long - No need to worry. For such case too, just answer the
							question in the best possible way, which is as per the actual number of Tax returns
							filed.
						</li>
					</ul>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 14,
		question: `Did you stay in India for 365 days or more in the last 4 Financial Years (${last4FY})?`,
		answers: [
			{ label: "Yes", value: 15 },
			{ label: "No", value: null, residentialStatus: "Non-Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<p className="primary-para text-greyDark mb-0">Tip:</p>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check the number of days stayed? - One way is to check your travel tickets and
							calculate the number of days you stayed in India during the last 4 Financial Years
							starting {last4FY}
						</li>
					</ul>
					<p className="primary-para text-greyDark mb-0">
						Select Yes if the days stayed in India in this time frame is more than or equal to 365.
					</p>
					<p className="primary-para text-greyDark">Select No if it is less than 365.</p>
					<p className="primary-para text-greyDark mb-0">
						Example: If your have stayed in India for 60-119 days in FY {partialFY} but less than
						365 days in the last 4 Financial Years, then as per the ITD rules, your residential
						status will be auto-selected as "Non-Resident".
					</p>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 15,
		question: `Were you a tax resident in India in more than 1 Financial Year out of 10 Financial Years prior to FY ${partialFY}?`,
		answers: [
			{ label: "Yes", value: 16 },
			{ label: "No", value: null, residentialStatus: "Resident but not Ordinary Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check if you were a tax resident in India for more than 1 out of the previous
							10 Financial Years?
							<ul>
								<li className="mb-3">
									Option 1: If you stayed in India for more than 181 days in each FY for minimum 2
									or more Financial Years before FY {partialFY}, then we can fairly suggest to
									select <b>Yes</b>. If this is true only for 0 or 1 FY, then select <b>No</b>.
								</li>
								<li className="mb-3">
									Option 2: Check your previous years' Income Tax Returns forms from E-filing portal
									and answer the question accordingly.
								</li>
							</ul>
						</li>
						<li className="mb-3">
							What if you have filed your tax returns for less than 10 Financial Years as you didn't
							have income for so long - No need to worry. For such case too, just answer the
							question in the best possible way, which is as per the actual number of Tax returns
							filed.
						</li>
					</ul>
				</Card.Body>
			</Card>
		)
	},
	{
		id: 16,
		question: `Were you in India for 730 days or more in the last 7 Financial Years (${last7FY})?`,
		answers: [
			{ label: "Yes", value: null, residentialStatus: "Resident" },
			{ label: "No", value: null, residentialStatus: "Resident but not Ordinary Resident" }
		],
		type: "radio",
		instructions: (
			<Card className="more-info-card shadow-none" bg="greyDefault">
				<Card.Body>
					<ul className="primary-para text-greyDark">
						<li className="mb-3">
							How to check the number of days stayed? - One way is to check your travel tickets and
							calculate the number of days you stayed in India during the last 7 Financial Years
							starting {last7FY}
						</li>
						<li className="mb-3">
							Select <b>Yes</b> if the days stayed in India in this time frame is more than or equal
							to 730.
						</li>
						<li className="mb-3">
							Select <b>No</b> if it is less than 730.
						</li>
					</ul>
					<p className="primary-para text-greyDark">
						Example 1: If your have stayed in India for 730 days or more between {last7FY}, then as
						per the ITD rules, your residential status will be auto-selected as "Resident".
					</p>
					<p className="primary-para text-greyDark mb-0">
						Example 2: If your have stayed in India for less than 730 days between {last7FY}, then
						as per the ITD rules, your residential status will be auto-selected as "Resident - Not
						Ordinary Resident".
					</p>
				</Card.Body>
			</Card>
		)
	}
	/* {
    id: 17,
    question:
      `Did you stay in India for 365 days or more in the last 4 Financial Years (${last4FY})?`,
    answers: [
      { label: "Yes", value: null, residentialStatus: "Resident" },
      { label: "No", value: null, residentialStatus: "Resident but not Ordinary Resident" }
    ],
    type: "radio",
    instructions: (
      <Card className="more-info-card shadow-none" bg="greyDefault">
        <Card.Body>
          <p className="primary-para text-greyDark mb-0">Tip:</p>
          <ul className="primary-para text-greyDark">
            <li className="mb-3">
              How to check the number of days stayed? - One way is to check your travel tickets and
              calculate the number of days you stayed in India during the last 4 Financial Years
              starting {last4FY}
            </li>
          </ul>
          <p className="primary-para text-greyDark mb-0">
            Select Yes if the days stayed in India in this time frame is more than or equal to 365.
          </p>
          <p className="primary-para text-greyDark">Select No if it is less than 365.</p>
          <p className="primary-para text-greyDark mb-0">
            Example: If your have stayed in India for 60-119 days in FY {partialFY} but less than 365
            days in the last 4 Financial Years, then as per the ITD rules, your residential status
            will be auto-selected as "Non-Resident".
          </p>
        </Card.Body>
      </Card>
    )
  } */
];

const ResidentialStatusForm: React.FC<Props> = (props: Props) => {
	const { showCanvas, handleCloseBtnPress } = props;

	const [currentQuestionId, setCurrentQuestionId] = useState<number | null>(1);
	const [selectedAnswers, setSelectedAnswers] = useState<ISelectedQueResp[]>([]);
	const [residentialStatus, setResidentialStatus] = useState<string | null>(null);
	const [currentQuestion, setCurrentQuestion] = useState<IResidentialQuestion | null>(null);

	useEffect(() => {
		return () => {
			setSelectedAnswers(() => []);
			setResidentialStatus(() => null);
			setCurrentQuestionId(() => null);
		};
	}, [showCanvas]);

	const handleAnswerSelection = (
		questionId: number,
		selectedAnswer: IResidentialQsnResponse,
		selectedValue: any // could be object or string
	) => {
		const answerIndex = selectedAnswers.findIndex((answer) => answer.questionId === questionId);

		if (answerIndex !== -1) {
			const updatedAnswers = selectedAnswers.slice(0, answerIndex);
			updatedAnswers.push({ questionId, selectedAnswer, selectedValue });
			setSelectedAnswers(updatedAnswers);
		} else {
			setSelectedAnswers((prev) => [...prev, { questionId, selectedAnswer, selectedValue }]);
		}

		if (selectedAnswer.value == null && selectedAnswer?.residentialStatus) {
			setResidentialStatus(selectedAnswer.residentialStatus);
			setCurrentQuestionId(null);
		} else {
			setResidentialStatus(null);
			setCurrentQuestionId(selectedAnswer.value);
		}
	};

	useEffect(() => {
		if (!currentQuestionId) {
			const defaultQuestion = {
				id: residentialQuestions[0].id,
				answers: residentialQuestions[0].answers,
				question: residentialQuestions[0].question,
				type: residentialQuestions[0].type,
				instructions: residentialQuestions[0].instructions
			};
			setCurrentQuestion(() => defaultQuestion);
			return;
		}

		const nextQuestion = residentialQuestions.find((q) => q.id === currentQuestionId);
		if (nextQuestion) {
			setCurrentQuestion(() => nextQuestion);
		}
	}, [currentQuestionId]);

	return (
		<>
			<Offcanvas show={showCanvas} placement="end" backdrop={true}>
				<>
					<Offcanvas.Header>
						<Offcanvas.Title>
							Residential Status
							<small>Residential Status is crucial to calculate the correct taxation.</small>
							<small>
								Just answer a few questions and <b>we will auto-select</b> the correct Residential
								Status for you!
							</small>
						</Offcanvas.Title>
						<Button
							variant="primary"
							className="icon-only hide-offcanvas"
							onClick={() => handleCloseBtnPress(null)}
						>
							<FASvgIcon icon={faXmark} />
						</Button>
					</Offcanvas.Header>
					<Offcanvas.Body>
						<>
							{selectedAnswers.map((answer, index) => {
								const question = residentialQuestions.find((q) => q.id === answer.questionId);
								return question ? (
									<section className="mb-5" key={`answers-${index}`}>
										<FormField label={question.question} sm={6}>
											{question.type === "dropdown" ? (
												<SelectInputField
													placeholder="Please Select "
													name={`select-${question?.id}`}
													value={answer?.selectedValue || ""}
													options={question.answers.map((ans) => ({
														label: ans.label,
														value: String(ans.value)
													}))}
													onChange={(selectedOption) => {
														const selectedOpt = question.answers.find(
															(q) => q.label === selectedOption.label
														);

														if (!selectedOpt) {
															return;
														}

														handleAnswerSelection(question.id, selectedOpt, selectedOpt);
													}}
													onBlur={() => null}
												/>
											) : (
												<ToggleButtonGroup
													type="radio"
													name={`rb-group-${question?.id}`}
													value={answer.selectedValue}
													className="rb-group-control"
												>
													{question.answers.map((ans, idx) => (
														<ToggleButton
															key={`answers-option-${idx}`}
															onClick={() => handleAnswerSelection(question.id, ans, ans.label)}
															variant="outline-primary"
															id={`rb-group-${question.id}-${idx}`}
															value={ans.label}
															className="btn-w-100 btn-h-40"
														>
															{ans.label}
														</ToggleButton>
													))}
												</ToggleButtonGroup>
											)}
										</FormField>
										{question?.instructions}
									</section>
								) : null;
							})}
						</>

						<>
							{currentQuestion && !residentialStatus && (
								<section className="mb-5">
									<FormField label={currentQuestion.question} sm={6}>
										{currentQuestion.type === "dropdown" ? (
											<SelectInputField
												placeholder="Please Select "
												name={`select-${currentQuestion?.id}`}
												options={currentQuestion.answers.map((ans) => ({
													label: ans.label,
													value: String(ans.value)
												}))}
												onChange={(selectedOption) => {
													const selectedOpt = currentQuestion.answers.find(
														(q) => q.label === selectedOption.label
													);

													if (!selectedOpt) {
														return;
													}
													handleAnswerSelection(currentQuestion.id, selectedOpt, selectedOpt);
												}}
												onBlur={() => null}
											/>
										) : (
											<ToggleButtonGroup
												type="radio"
												name={`rb-group-${currentQuestion.id}`}
												className="rb-group-control"
											>
												{currentQuestion.answers.map((ans, index) => (
													<ToggleButton
														key={`current-que-${index}`}
														onClick={() =>
															handleAnswerSelection(currentQuestion.id, ans, ans.label)
														}
														variant="outline-primary"
														id={`rb-group-${currentQuestion.id}-${index}`}
														value=""
														className="btn-w-100 btn-h-40"
													>
														{ans.label}
													</ToggleButton>
												))}
											</ToggleButtonGroup>
										)}
									</FormField>
									{currentQuestion?.instructions}
								</section>
							)}
						</>

						{!currentQuestionId && residentialStatus && (
							<h2 className="text-center text-primaryDark fw-bold my-5">{residentialStatus}</h2>
						)}
					</Offcanvas.Body>

					<div className="offcanvas-btns">
						<Button
							variant="outline-white"
							size="sm"
							onClick={() => handleCloseBtnPress(residentialStatus)}
						>
							Save and Proceed
						</Button>
					</div>
				</>
			</Offcanvas>
		</>
	);
};

export default ResidentialStatusForm;
